import { DRUNK_AT, stageOf, TIPSY_AT, type BarState } from '../engine/bar';
import { cardLabel, suitOf, type Card } from '../engine/cards';
import { money } from '../format';

function buzzCells(bar: BarState): string[] {
  const { buzz, maxBuzz, tipsyAt = TIPSY_AT, drunkAt = DRUNK_AT } = bar;
  return Array.from({ length: maxBuzz }, (_, i) => {
    const n = i + 1;
    const zone = n >= drunkAt ? 'drunk' : n >= tipsyAt ? 'tipsy' : 'sober';
    // Each filled segment takes its zone's colour, so crossing into Drunk
    // shows red (a filled Drunk segment used to be Tipsy gold, which made
    // Buzz 7 look like Tipsy while the Drunk rules applied).
    if (n > buzz) return `empty-${zone}`;
    return zone;
  });
}

export function buzzStage(bar: BarState): string {
  return stageOf(bar.buzz, bar.maxBuzz, bar.tipsyAt ?? TIPSY_AT, bar.drunkAt ?? DRUNK_AT);
}

/** The Buzz block on the hub. */
export function BuzzMeter({ bar }: { bar: BarState }) {
  return (
    <div className="block buzz">
      <div className="label">
        <span>Buzz {bar.buzz}</span>
        <span className={`stage ${buzzStage(bar).toLowerCase()}`}>{buzzStage(bar)}</span>
      </div>
      <div className="meter" style={{ ['--cells' as string]: bar.maxBuzz }} aria-hidden="true">
        {buzzCells(bar).map((c, i) => <span key={i} className={c} />)}
      </div>
    </div>
  );
}

/** The slim Buzz line in a table's top bar. */
export function BuzzLine({ bar }: { bar: BarState }) {
  return (
    <div className="buzz-line label">
      <span>Buzz {bar.buzz}</span>
      <div className="meter slim" style={{ ['--cells' as string]: bar.maxBuzz }} aria-hidden="true">
        {buzzCells(bar).map((c, i) => <span key={i} className={c} />)}
      </div>
      <span className={`stage ${buzzStage(bar).toLowerCase()}`}>{buzzStage(bar)}</span>
    </div>
  );
}

export function PlayingCard({ card, hidden, ring, lost }: { card?: Card; hidden?: boolean; ring?: boolean; lost?: boolean }) {
  if (hidden || card === undefined) return <div className="pcard back" aria-label="Face-down card" />;
  const label = cardLabel(card);
  const rank = label.slice(0, -1);
  const suit = label.slice(-1);
  const red = suitOf(card) === 1 || suitOf(card) === 2;
  return (
    <div className={`pcard${red ? ' red' : ''}${ring ? ' ring' : ''}${lost ? ' lost' : ''}`} aria-label={label}>
      <span className="rank">{rank}</span>
      <span className="suit">{suit}</span>
    </div>
  );
}

const RANK_LABELS = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];
const SUIT_LABELS = ['♠', '♥', '♦', '♣'];

/** A card back from the engine's label ("10♥"), or null if it isn't one. */
export function parseCard(label: string): Card | null {
  const rank = RANK_LABELS.indexOf(label.slice(0, -1));
  const suit = SUIT_LABELS.indexOf(label.slice(-1));
  return rank < 0 || suit < 0 ? null : suit * 13 + rank;
}

/** Every card label on a line of engine text, in order. */
export function cardsIn(line: string): Card[] {
  return line.split(/\s+/).map(parseCard).filter((c): c is Card => c !== null);
}

const PIPS: Record<number, [number, number][]> = {
  1: [[50, 50]],
  2: [[28, 28], [72, 72]],
  3: [[26, 26], [50, 50], [74, 74]],
  4: [[28, 28], [72, 28], [28, 72], [72, 72]],
  5: [[26, 26], [74, 26], [50, 50], [26, 74], [74, 74]],
  6: [[28, 24], [72, 24], [28, 50], [72, 50], [28, 76], [72, 76]],
};

/** A die: its face when shown, a plain hidden die otherwise. */
export function Die({ face, hidden, hot, size }: { face?: number; hidden?: boolean; hot?: boolean; size?: string }) {
  return (
    <svg viewBox="0 0 100 100" className={`die${hidden ? ' hidden' : ''}${hot ? ' hot' : ''}`} style={size ? { width: size, height: size } : undefined} role="img" aria-label={hidden || !face ? 'Hidden die' : `${face}`}>
      <rect x="4" y="4" width="92" height="92" rx="18" className="die-body" />
      {!hidden && face ? PIPS[face]!.map(([x, y], i) => <circle key={i} cx={x} cy={y} r="9" className="die-pip" />) : <text x="50" y="64" textAnchor="middle" className="die-q">?</text>}
    </svg>
  );
}

/** − chip + : steps through the table's bet ladder (only amounts you can cover). */
/** The − chip + bet row. `locked` (mid-play, or while something plays) keeps
 *  it on screen showing the bet riding, so the controls never change height
 *  and the table never jumps (user, 2026-09-27). */
export function BetControl({ bet, ladder, onChange, locked = false }: { bet: number; ladder: number[]; onChange: (bet: number) => void; locked?: boolean }) {
  const i = Math.max(0, ladder.indexOf(bet));
  const lower = locked ? undefined : ladder[i - 1];
  const higher = locked ? undefined : ladder[i + 1];
  return (
    <div className={`bet-control${locked ? ' locked' : ''}`}>
      <button className="step" disabled={lower === undefined} onClick={() => lower !== undefined && onChange(lower)} aria-label="Lower bet">−</button>
      <div className="chip" aria-label={`Bet ${money(bet)}${locked ? ', riding' : ''}`}>{money(bet)}</div>
      <button className="step" disabled={higher === undefined} onClick={() => higher !== undefined && onChange(higher)} aria-label="Raise bet">+</button>
    </div>
  );
}
